import { applySink } from './useLocalMedia';

/**
 * Play a short tone through a specific output device.
 *
 * A speaker picker you cannot hear is guesswork — especially with three
 * near-identically named outputs, which is the normal case once a monitor and a
 * headset are plugged in. The tone is synthesised rather than shipped as an
 * audio file so there is no asset to load and nothing to cache.
 *
 * Routing goes through an <audio> element rather than `AudioContext.setSinkId`,
 * which Chrome only gained recently and Firefox does not have at all.
 */
export async function playTestTone(sinkId: string | undefined): Promise<void> {
  const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return;

  const ctx = new AudioCtx();
  const destination = ctx.createMediaStreamDestination();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = 'sine';
  osc.frequency.value = 440;

  // Ramp in and out: a square-edged start and stop on a sine wave produces an
  // audible click at both ends.
  const now = ctx.currentTime;
  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(0.15, now + 0.02);
  gain.gain.setValueAtTime(0.15, now + 0.35);
  gain.gain.linearRampToValueAtTime(0, now + 0.4);

  osc.connect(gain).connect(destination);

  const el = document.createElement('audio');
  el.srcObject = destination.stream;
  await applySink(el, sinkId);

  osc.start(now);
  osc.stop(now + 0.42);

  try {
    await el.play();
  } catch {
    /* autoplay refused; nothing useful to do but stay quiet */
  }

  await new Promise((resolve) => setTimeout(resolve, 500));
  el.pause();
  el.srcObject = null;
  await ctx.close();
}
